import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { 
  Trophy, AlertCircle, CheckCircle2, XCircle, Loader2, 
  FileText, Lightbulb, TrendingUp, RotateCcw, Copy, Check,
  AlertTriangle, ShieldAlert, Award
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Certificate } from "./Certificate";

interface AssessmentResult {
  score: number;
  passed: boolean;
  breakdown: {
    documentStructure: { score: number; feedback: string };
    textFormatting: { score: number; feedback: string };
    mathematicalContent: { score: number; feedback: string };
    tablesAndLists: { score: number; feedback: string };
    professionalQuality: { score: number; feedback: string };
  };
  strengths: string[];
  improvements: string[];
  nextSteps: string;
  overallFeedback: string;
  certificateNumber?: string;
}

interface CertificateData {
  id: string;
  score: number;
  passed: boolean;
  certificate_number: string | null;
  issued_at: string | null;
  cheating_warnings: number;
  is_disqualified: boolean;
}

const STARTER_TEMPLATE = `\\documentclass{article}
\\usepackage{amsmath}
\\usepackage{graphicx}

\\title{Your Report Title Here}
\\author{Your Name}
\\date{\\today}

\\begin{document}

\\maketitle

\\section{Introduction}
Write your introduction here. Explain the purpose and scope of your report.

\\section{Methodology}
Describe your methods or approach.

\\subsection{Data Collection}
Explain how data was collected.

\\section{Results}
Present your findings with mathematical notation:

\\begin{equation}
E = mc^2
\\end{equation}

Include a table of results:

\\begin{tabular}{|c|c|c|}
\\hline
Item & Value & Unit \\\\
\\hline
Mass & 5.0 & kg \\\\
Velocity & 10.0 & m/s \\\\
Energy & 250.0 & J \\\\
\\hline
\\end{tabular}

\\section{Discussion}
\\begin{itemize}
  \\item Key finding 1
  \\item Key finding 2
  \\item Key finding 3
\\end{itemize}

\\section{Conclusion}
Summarize your conclusions here.

\\end{document}`;

export function FinalProject() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [latex, setLatex] = useState("");
  const [isAssessing, setIsAssessing] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [certificateData, setCertificateData] = useState<CertificateData | null>(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [cheatingWarning, setCheatingWarning] = useState<string | null>(null);
  const [isDisqualified, setIsDisqualified] = useState(false);

  // Fetch existing certificate data
  useEffect(() => {
    const fetchCertificate = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('latex_certificates')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data && !error) {
        setCertificateData(data);
        if (data.is_disqualified) {
          setIsDisqualified(true);
        }
        if (data.cheating_warnings > 0 && !data.is_disqualified) {
          setCheatingWarning(t('latexCenter.finalProject.warningReminder', 'You have received a warning for attempting to use AI. One more attempt will result in disqualification.'));
        }
      }
    };

    fetchCertificate();
  }, [user, t]);

  const handleSubmit = async () => {
    if (!latex.trim()) {
      toast.error(t('latexCenter.finalProject.emptyError', 'Please enter your LaTeX code'));
      return;
    }

    if (isDisqualified) {
      toast.error(t('latexCenter.finalProject.disqualifiedError', 'You have been disqualified. Please contact support.'));
      return;
    }

    setIsAssessing(true);
    setResult(null);
    setCheatingWarning(null);

    try {
      const { data, error } = await supabase.functions.invoke('assess-latex-project', {
        body: { latex, projectType: 'Professional Report' }
      });

      if (error) {
        throw new Error(error.message);
      }

      // Handle cheating detection responses
      if (data.error === 'cheating_warning') {
        setCheatingWarning(data.warning);
        toast.error(t('latexCenter.finalProject.cheatingDetected', 'AI usage detected'));
        return;
      }

      if (data.error === 'disqualified') {
        setIsDisqualified(true);
        toast.error(data.message);
        return;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setResult(data);
      
      // Update certificate data if passed
      if (data.passed && data.certificateNumber) {
        setCertificateData(prev => ({
          ...prev,
          id: prev?.id || '',
          score: data.score,
          passed: true,
          certificate_number: data.certificateNumber,
          issued_at: new Date().toISOString(),
          cheating_warnings: prev?.cheating_warnings || 0,
          is_disqualified: false
        }));
      }
      
      if (data.passed) {
        toast.success(t('latexCenter.finalProject.passedToast', 'Congratulations! You passed!'));
      } else {
        toast.info(t('latexCenter.finalProject.failedToast', 'Keep practicing! Check the feedback below.'));
      }
    } catch (error) {
      console.error('Assessment error:', error);
      toast.error(error instanceof Error ? error.message : 'Assessment failed');
    } finally {
      setIsAssessing(false);
    }
  };

  const loadTemplate = () => {
    setLatex(STARTER_TEMPLATE);
    toast.success(t('latexCenter.finalProject.templateLoaded', 'Template loaded!'));
  };

  const copyTemplate = () => {
    navigator.clipboard.writeText(STARTER_TEMPLATE);
    setCopied(true);
    toast.success(t('latexCenter.finalProject.templateCopied', 'Template copied!'));
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    setLatex("");
    setResult(null);
    setCheatingWarning(null);
  };

  const getCategoryLabel = (key: string) => {
    const labels: Record<string, string> = {
      documentStructure: t('latexCenter.finalProject.categories.documentStructure', 'Document Structure'),
      textFormatting: t('latexCenter.finalProject.categories.textFormatting', 'Text Formatting'),
      mathematicalContent: t('latexCenter.finalProject.categories.mathematicalContent', 'Mathematical Content'),
      tablesAndLists: t('latexCenter.finalProject.categories.tablesAndLists', 'Tables & Lists'),
      professionalQuality: t('latexCenter.finalProject.categories.professionalQuality', 'Professional Quality'),
    };
    return labels[key] || key;
  };

  // Show certificate view
  if (showCertificate && certificateData?.certificate_number && certificateData?.issued_at) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => setShowCertificate(false)}>
          ← {t('latexCenter.finalProject.backToProject', 'Back to Project')}
        </Button>
        <Certificate
          userName={user?.email?.split('@')[0] || 'Student'}
          score={certificateData.score}
          certificateNumber={certificateData.certificate_number}
          issuedAt={certificateData.issued_at}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Existing Certificate Banner */}
      {certificateData?.passed && certificateData?.certificate_number && (
        <Alert className="border-green-500 bg-green-500/10">
          <Award className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-700 dark:text-green-400">
            {t('latexCenter.finalProject.alreadyPassed', 'You\'ve Already Earned Your Certificate!')}
          </AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{t('latexCenter.finalProject.viewCertificate', 'View and download your completion certificate.')}</span>
            <Button size="sm" onClick={() => setShowCertificate(true)} className="bg-green-600 hover:bg-green-700">
              <Award className="h-4 w-4 mr-1" />
              {t('latexCenter.finalProject.viewCertificateBtn', 'View Certificate')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Disqualification Alert */}
      {isDisqualified && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>{t('latexCenter.finalProject.disqualified', 'Disqualified')}</AlertTitle>
          <AlertDescription>
            {t('latexCenter.finalProject.disqualifiedMessage', 'You have been disqualified for attempting to use AI to generate your report. Please contact support if you believe this is an error.')}
          </AlertDescription>
        </Alert>
      )}

      {/* Cheating Warning Alert */}
      {cheatingWarning && !isDisqualified && (
        <Alert className="border-amber-500 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-400">
            {t('latexCenter.finalProject.warningTitle', 'Warning: AI Usage Detected')}
          </AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            {cheatingWarning}
          </AlertDescription>
        </Alert>
      )}

      {/* Project Brief */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {t('latexCenter.finalProject.title', 'Final Project: Professional Report')}
          </CardTitle>
          <CardDescription>
            {t('latexCenter.finalProject.description', 'Create a complete LaTeX report demonstrating all the skills you\'ve learned. Your report will be assessed by SuperCalc AI.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Important Notice */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('latexCenter.finalProject.importantNotice', 'Important')}</AlertTitle>
            <AlertDescription>
              {t('latexCenter.finalProject.honestWork', 'You must write your own LaTeX code. Using AI to generate your report will result in disqualification. You have one warning before being disqualified.')}
            </AlertDescription>
          </Alert>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">{t('latexCenter.finalProject.requirements', 'Requirements')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ {t('latexCenter.finalProject.req1', 'Document class and preamble')}</li>
                <li>✓ {t('latexCenter.finalProject.req2', 'Title, author, and date')}</li>
                <li>✓ {t('latexCenter.finalProject.req3', 'At least 3 sections with subsections')}</li>
                <li>✓ {t('latexCenter.finalProject.req4', 'Mathematical equations')}</li>
                <li>✓ {t('latexCenter.finalProject.req5', 'At least one table')}</li>
                <li>✓ {t('latexCenter.finalProject.req6', 'Bulleted or numbered lists')}</li>
                <li>✓ {t('latexCenter.finalProject.req7', 'Text formatting (bold, italic)')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">{t('latexCenter.finalProject.passRate', 'Pass Rate')}</h4>
              <div className="flex items-center gap-3">
                <Progress value={80} className="flex-1 h-3" />
                <span className="text-lg font-bold text-primary">80%</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {t('latexCenter.finalProject.passDescription', 'Score at least 80 out of 100 points to pass')}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadTemplate} disabled={isDisqualified}>
              <FileText className="h-4 w-4 mr-1" />
              {t('latexCenter.finalProject.loadTemplate', 'Load Starter Template')}
            </Button>
            <Button variant="ghost" size="sm" onClick={copyTemplate}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {t('latexCenter.finalProject.copyTemplate', 'Copy Template')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input Area */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('latexCenter.finalProject.yourCode', 'Your LaTeX Code')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={latex}
            onChange={(e) => setLatex(e.target.value)}
            placeholder={t('latexCenter.finalProject.placeholder', 'Paste or write your complete LaTeX document here...')}
            className="font-mono text-sm min-h-[300px]"
            disabled={isDisqualified}
          />
          
          <div className="flex gap-2">
            <Button 
              onClick={handleSubmit} 
              disabled={isAssessing || !latex.trim() || isDisqualified}
              className="flex-1 md:flex-none"
            >
              {isAssessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('latexCenter.finalProject.assessing', 'Assessing...')}
                </>
              ) : (
                <>
                  <Trophy className="h-4 w-4 mr-2" />
                  {t('latexCenter.finalProject.submit', 'Submit for Assessment')}
                </>
              )}
            </Button>
            {(latex || result) && (
              <Button variant="outline" onClick={reset} disabled={isDisqualified}>
                <RotateCcw className="h-4 w-4 mr-1" />
                {t('latexCenter.finalProject.reset', 'Reset')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className={cn(
          "border-2 transition-all",
          result.passed 
            ? "border-green-500 bg-green-500/5" 
            : "border-amber-500 bg-amber-500/5"
        )}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                {result.passed ? (
                  <>
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                    <span className="text-green-700 dark:text-green-400">
                      {t('latexCenter.finalProject.passed', 'Congratulations! You Passed!')}
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-6 w-6 text-amber-600" />
                    <span className="text-amber-700 dark:text-amber-400">
                      {t('latexCenter.finalProject.notPassed', 'Not Quite There Yet')}
                    </span>
                  </>
                )}
              </CardTitle>
              <Badge 
                variant={result.passed ? "default" : "secondary"}
                className={cn(
                  "text-lg px-4 py-1",
                  result.passed ? "bg-green-600" : "bg-amber-600"
                )}
              >
                {result.score}/100
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Certificate Button for passed users */}
            {result.passed && result.certificateNumber && (
              <Alert className="border-green-500 bg-green-600/20">
                <Award className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-700 dark:text-green-400">
                  {t('latexCenter.finalProject.certificateReady', 'Your Certificate is Ready!')}
                </AlertTitle>
                <AlertDescription className="flex items-center justify-between">
                  <span>{t('latexCenter.finalProject.certificateDownload', 'Download your completion certificate as PNG or PDF.')}</span>
                  <Button 
                    size="sm" 
                    onClick={() => {
                      setCertificateData(prev => ({
                        ...prev!,
                        certificate_number: result.certificateNumber!,
                        issued_at: new Date().toISOString()
                      }));
                      setShowCertificate(true);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Award className="h-4 w-4 mr-1" />
                    {t('latexCenter.finalProject.getCertificate', 'Get Certificate')}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Overall Feedback */}
            <div className="p-4 bg-background rounded-lg">
              <p className="text-foreground">{result.overallFeedback}</p>
            </div>

            {/* Score Breakdown */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('latexCenter.finalProject.breakdown', 'Score Breakdown')}
              </h4>
              <div className="grid gap-3">
                {Object.entries(result.breakdown).map(([key, value]) => (
                  <div key={key} className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{getCategoryLabel(key)}</span>
                      <Badge variant={value.score >= 16 ? "default" : value.score >= 10 ? "secondary" : "destructive"}>
                        {value.score}/20
                      </Badge>
                    </div>
                    <Progress value={(value.score / 20) * 100} className="h-2 mb-2" />
                    <p className="text-sm text-muted-foreground">{value.feedback}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Strengths */}
            {result.strengths.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('latexCenter.finalProject.strengths', 'What You Did Well')}
                </h4>
                <ul className="space-y-1">
                  {result.strengths.map((strength, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas for Improvement */}
            {result.improvements.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4" />
                  {t('latexCenter.finalProject.improvements', 'Areas for Improvement')}
                </h4>
                <ul className="space-y-1">
                  {result.improvements.map((improvement, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-amber-600">→</span>
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next Steps */}
            <div className="p-4 bg-primary/10 rounded-lg">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                {t('latexCenter.finalProject.nextSteps', 'Next Steps')}
              </h4>
              <p className="text-sm">{result.nextSteps}</p>
            </div>

            {!result.passed && (
              <Button onClick={reset} className="w-full">
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('latexCenter.finalProject.tryAgain', 'Try Again')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
